import {
    Add,
    AlertModal,
    Box,
    Button,
    Checkbox,
    ContextualBanner,
    Delete,
    Heading,
    IconButton,
    Info,
    Modal,
    Select,
    showToast,
    SubPageLayout,
    TextBlock,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { Form, Formik, FormikHelpers, FormikProps, getIn } from "formik";
import ipaddr from "ipaddr.js";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { array, boolean, number, object, string } from "yup";

import style from "./shared-settings-form.scss";
import { getSessionDurationOptions } from "components/licenses/common";
import { AUTH_SSO_CONFIGURE, AUTH_TENANT_SETTINGS } from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { LoginMethod, loginMethodList, Tenant } from "domain/tenants";
import * as settingRepository from "services/settings/settingsRepository";
import { getCurrentTenantDetails, getTenantUuid, hasTenantCookie } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { TenantDetails, updateTenantDetails } from "store/tenantDetails";
import { setUser } from "store/user";
import { isStringBlank } from "utils/commonFunctions";

import testIds from "testIds.json";

export interface FormValues {
    statistics: boolean;
    loginMethod: LoginMethod;
    sessionDuration: number;
    newsEmailAlerts: boolean;
    cidrs: string[];
}

export const SHARED_SETTINGS_FORM_ID = "shared-settings-form";
const DEFAULT_LOGIN_METHOD = "COMPANY_ID_AND_EMAIL";
const DEFAULT_REFRESH_TOKEN_EXPIRATION = 30 * 24 * 60 * 60;
const MAX_CIDR_BLOCK_FIELDS = 10;

const LOGIN_METHOD_TRANSLATION_KEYS: Record<LoginMethod, string> = {
    COMPANY_ID: "Settings.loginSettings.loginMethod.companyId",
    EMAIL: "Settings.loginSettings.loginMethod.emailSignIn",
    COMPANY_ID_AND_EMAIL: "Settings.loginSettings.loginMethod.both",
};

export const SharedSettingsForm = () => {
    const { t } = useTranslation();
    const sessionDurationOptions = getSessionDurationOptions(t);
    const [tenant, setTenant] = React.useState<Tenant>();
    const [ssoConfigurationFound, setSsoConfigurationFound] = React.useState<boolean>();
    const [isCancelModalVisible, setIsCancelModalVisible] = React.useState<boolean>(false);
    const [isAlertModalVisible, setAlertModalVisible] = React.useState<boolean>(false);
    const [loadingFailed, setLoadingFailed] = React.useState<boolean>(false);
    const [isLoadingFailedModalVisible, setLoadingFailedModalVisible] = React.useState<boolean>(false);
    const [alertModalMessage, setAlertModalMessage] = React.useState<string | undefined>(undefined);
    const isMobileResolution = window.innerWidth < Breakpoint.MD;

    const dispatch = useDispatch();
    const formikRef = React.useRef<FormikProps<FormValues>>(null);
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const tenantDetails = useSelector(
        (state: StoreState) => state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1]
    );

    useEffect(() => {
        if (user == null) {
            return;
        }

        const abortController = new AbortController();
        const tenantUuid = hasTenantCookie() ? getTenantUuid() : user.tenantUuid;
        tenantService
            .fetchTenant(tenantUuid, abortController)
            .then(setTenant)
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setLoadingFailed(true);
                    setLoadingFailedModalVisible(true);
                }
            });
        return () => abortController.abort();
    }, [user?.tenantUuid]);

    useEffect(() => {
        if (!tenantDetails.uuid) {
            setSsoConfigurationFound(false);
            return;
        }

        const abortController = new AbortController();
        userService
            .fetchSsoSettings(tenantDetails.uuid, undefined, abortController)
            .then((ssoConfiguration) => setSsoConfigurationFound(ssoConfiguration != null))
            // The request fails when the tenant has no SSO configuration, so a failure isn't an error here.
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setSsoConfigurationFound(false);
                }
            });
        return () => abortController.abort();
    }, [tenantDetails.uuid]);

    const getValidSessionDuration = (duration: number | undefined): number => {
        if (!duration) {
            return DEFAULT_REFRESH_TOKEN_EXPIRATION;
        }

        const exactMatch = sessionDurationOptions.find((option) => option.value === duration);
        if (exactMatch) {
            return duration;
        }

        return DEFAULT_REFRESH_TOKEN_EXPIRATION;
    };

    if (loadingFailed) {
        return (
            <>
                <SubPageLayout
                    hideCancelButton={true}
                    hideBackArrow={true}
                    padding="None"
                    className={style.subPageLayout}
                />

                <AlertModal
                    title={t("Settings.errorTitle")}
                    variant={"Error"}
                    isOpen={isLoadingFailedModalVisible}
                    buttons={
                        <Button
                            variant="Primary"
                            onClick={() => setLoadingFailedModalVisible(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.ok")}
                        </Button>
                    }
                    hideModal={() => setLoadingFailedModalVisible(false)}
                >
                    {t("Common.failureMessage")}
                </AlertModal>
            </>
        );
    }

    if (user == null || tenant == null || ssoConfigurationFound == null) {
        return (
            <SubPageLayout
                loading={true}
                hideCancelButton={true}
                hideBackArrow={true}
                padding="None"
                className={style.subPageLayout}
            />
        );
    }

    const isValidCidr = (cidr: string): boolean => {
        try {
            if (!cidr.includes("/")) {
                // If there's no '/', treat it as an IP address
                return ipaddr.isValid(cidr);
            }

            const [ip, prefix] = cidr.split("/");

            if (!ipaddr.isValid(ip)) {
                return false; // Invalid IP address
            }

            const prefixNum = parseInt(prefix, 10);
            if (isNaN(prefixNum)) {
                return false; // Prefix is not a number
            }

            const maxPrefix = ipaddr.parse(ip).kind() === "ipv4" ? 32 : 128;
            return prefixNum >= 0 && prefixNum <= maxPrefix;
        } catch {
            return false;
        }
    };

    const initialValues: FormValues = {
        sessionDuration: getValidSessionDuration(tenant.refreshTokenExpiration),
        statistics: settingRepository.getDataCollection() ?? user.usageStatistics,
        loginMethod:
            tenant.loginMethod == null || isStringBlank(tenant.loginMethod) ? DEFAULT_LOGIN_METHOD : tenant.loginMethod,
        newsEmailAlerts:
            tenantDetails.contractType !== "TRIAL" && user.role.default_migration_role
                ? (tenant.newsEmailOptIn ?? true)
                : false,
        cidrs: tenant.ipRestrictions,
    };

    const validationSchema = object().shape({
        statistics: boolean().required(),
        loginMethod: string().required().oneOf(loginMethodList),
        sessionDuration: number()
            .required()
            .oneOf(sessionDurationOptions.map((option) => option.value)),
        newsEmailAlerts: boolean().required(),
        cidrs: array()
            .of(
                string()
                    .trim()
                    .required(t("Settings.cidrConfiguration.ipValidationError"))
                    .test("valid-cidr", t("Settings.cidrConfiguration.ipValidationError"), (value) =>
                        isValidCidr(value ?? "")
                    )
                    .test(
                        "duplicate-cidr",
                        t("Settings.cidrConfiguration.duplicateCidrError"),
                        (value, context) => context.path === `cidrs[${context.parent.indexOf(value)}]`
                    )
            )
            .max(MAX_CIDR_BLOCK_FIELDS),
    });

    const submitSettings = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        // The tenant state behind initialValues is fetched once on mount and isn't refreshed after saving, so comparing
        // is done against Formik's initial values, which resetForm updates on success, ensuring that values stay in sync
        if (formikRef.current == null) {
            throw new Error("Formik is not mounted");
        }
        const savedValues = formikRef.current.initialValues;

        const abortController = new AbortController();
        const tenantUuid = hasTenantCookie() ? getTenantUuid() : user.tenantUuid;

        const statisticsChanged = values.statistics !== savedValues.statistics;
        const loginMethodChanged = values.loginMethod !== savedValues.loginMethod;
        const sessionDurationChanged = values.sessionDuration !== savedValues.sessionDuration;
        const newsEmailAlertsChanged = values.newsEmailAlerts !== savedValues.newsEmailAlerts;
        const cidrs = values.cidrs.map((cidr) => cidr.trim());

        try {
            await tenantService.updateTenantSettings(
                tenantUuid,
                statisticsChanged ? values.statistics : undefined,
                loginMethodChanged ? values.loginMethod : undefined,
                cidrs,
                sessionDurationChanged ? values.sessionDuration : undefined,
                newsEmailAlertsChanged ? values.newsEmailAlerts : undefined,
                abortController
            );

            if (statisticsChanged) {
                settingRepository.setDataCollection(values.statistics);
            }

            if (loginMethodChanged || sessionDurationChanged || newsEmailAlertsChanged) {
                if (user && newsEmailAlertsChanged) {
                    dispatch(
                        setUser({
                            ...user,
                            default_settings: {
                                ...user.default_settings,
                                news_email_opt_in: values.newsEmailAlerts,
                            },
                        })
                    );
                }

                const updatedTenantDetails: TenantDetails = {
                    ...getCurrentTenantDetails(),
                    ...(loginMethodChanged && { loginMethod: values.loginMethod }),
                    ...(sessionDurationChanged && { refreshTokenExpiration: values.sessionDuration }),
                    ...(newsEmailAlertsChanged && { newsEmailOptIn: values.newsEmailAlerts }),
                };
                dispatch(updateTenantDetails(updatedTenantDetails));
            }

            const persistedValues: FormValues = { ...values, cidrs: cidrs };
            helpers.resetForm({ values: persistedValues });
            showToast(t("Settings.successMessage"), "success");
        } catch (error) {
            if (!abortController.signal.aborted) {
                const errorWithStatus = error as Error & { status?: number };
                setAlertModalMessage(
                    errorWithStatus?.status === 504 ? t("Settings.timeoutErrorMessage") : t("Settings.errorMessage")
                );
                setAlertModalVisible(true);
            }
        }
    };

    return (
        <>
            <Formik<FormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                innerRef={formikRef}
                onSubmit={submitSettings}
            >
                {({
                    values,
                    dirty,
                    setFieldValue,
                    submitCount,
                    errors,
                    isSubmitting,
                    isValid,
                }: FormikProps<FormValues>) => {
                    return (
                        <SubPageLayout
                            buttons={
                                <>
                                    <Button
                                        variant="Primary"
                                        type="submit"
                                        form={SHARED_SETTINGS_FORM_ID}
                                        isLoading={isSubmitting}
                                        loadingText={t("Common.saving")}
                                        disabled={!dirty || (!isValid && submitCount > 0)}
                                        testId={testIds.workArea.settings.shared.saveButton}
                                    >
                                        {t("Common.save")}
                                    </Button>

                                    <Button
                                        variant="Secondary"
                                        onClick={() => setIsCancelModalVisible(true)}
                                        disabled={isSubmitting || !dirty}
                                        testId={testIds.workArea.settings.shared.cancelButton}
                                    >
                                        {t("Common.cancel")}
                                    </Button>
                                </>
                            }
                            footerBanner={{ visible: !isValid && submitCount > 0, text: t("Common.fixErrors") }}
                            hideCancelButton
                            hideBackArrow
                            padding="None"
                            className={style.subPageLayout}
                        >
                            <Form id={SHARED_SETTINGS_FORM_ID}>
                                <Box
                                    columnLayout
                                    paddingTop="Md"
                                    paddingBottom="Md"
                                    gap="Lg"
                                    className={style.formContent}
                                >
                                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                                        userSessionService.userHasAllAuthorities([AUTH_TENANT_SETTINGS]) &&
                                        !hasTenantCookie() && (
                                            <Box columnLayout>
                                                <Heading variant="h2" underline>
                                                    {t("Settings.sessionSettings.title")}
                                                </Heading>

                                                <ContextualBanner variant="Warning" className={style.banner}>
                                                    {t("Settings.sessionSettings.warningBanner")}
                                                </ContextualBanner>

                                                <Box paddingTop="Xs" gap="Sm">
                                                    <Select
                                                        key={values.sessionDuration}
                                                        name="session_duration"
                                                        label={`${t("Settings.sessionSettings.title")}:`}
                                                        labelAbove={isMobileResolution}
                                                        onSelection={(value) => {
                                                            setFieldValue("sessionDuration", Number(value));
                                                        }}
                                                        items={sessionDurationOptions.map((option) => ({
                                                            label: option.label,
                                                            value: option.value.toString(),
                                                        }))}
                                                        selectedByDefault={values.sessionDuration.toString()}
                                                        placeholder={t("Common.select")}
                                                        disabled={isSubmitting}
                                                        testId={testIds.workArea.settings.shared.sessionDurationSelect}
                                                    />

                                                    <Tooltip placement="right">
                                                        <TooltipContent>
                                                            {t("Settings.sessionSettings.sessionSettingTooltip")}
                                                        </TooltipContent>
                                                        <TooltipTrigger
                                                            className={isMobileResolution ? style.tooltip : undefined}
                                                        >
                                                            <Info size={20} />
                                                        </TooltipTrigger>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        )}

                                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                                        userSessionService.userHasAllAuthorities([AUTH_TENANT_SETTINGS]) &&
                                        !hasTenantCookie() && (
                                            <Box columnLayout>
                                                <Heading variant="h2" underline>
                                                    {t("Settings.statisticsTitle")}
                                                </Heading>

                                                <ContextualBanner variant={"Warning"} className={style.banner}>
                                                    {t("Settings.warning")}
                                                </ContextualBanner>

                                                <Box paddingTop="Sm" verticalAlignment="Center" gap="Xs">
                                                    {t("Settings.gatherStatistics")}

                                                    <Checkbox
                                                        name="statistics"
                                                        checked={values.statistics}
                                                        onChange={(e) => setFieldValue("statistics", e.target.checked)}
                                                        disabled={user.tenantType === "INTERNAL" || isSubmitting}
                                                        testId={testIds.workArea.settings.shared.statisticsCheckbox}
                                                    />

                                                    <Tooltip placement="right">
                                                        <TooltipContent>{t("Settings.warningTooltip")}</TooltipContent>
                                                        <TooltipTrigger>
                                                            <Info size={20} />
                                                        </TooltipTrigger>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        )}

                                    {tenantDetails.featureLicenses.includes("FEATURE_SSO") &&
                                        userSessionService.userHasAllAuthorities([
                                            AUTH_TENANT_SETTINGS,
                                            AUTH_SSO_CONFIGURE,
                                        ]) &&
                                        ssoConfigurationFound && (
                                            <Box columnLayout>
                                                <Heading variant="h2" underline>
                                                    {t("Settings.loginSettings.title")}
                                                </Heading>

                                                <Box columnLayout>
                                                    <TextBlock>{t("Settings.loginSettings.effect")}</TextBlock>

                                                    <Select
                                                        key={values.loginMethod}
                                                        id="loginMethod"
                                                        name={"login_method"}
                                                        label={t("Settings.loginSettings.loginMethod.label")}
                                                        labelAbove={isMobileResolution}
                                                        placeholder={t("Common.select")}
                                                        items={loginMethodList.map((option) => ({
                                                            value: option,
                                                            label: t(LOGIN_METHOD_TRANSLATION_KEYS[option]),
                                                        }))}
                                                        selectedByDefault={values.loginMethod}
                                                        onSelection={(value) => {
                                                            setFieldValue("loginMethod", value);
                                                        }}
                                                        disabled={isSubmitting}
                                                        testId={testIds.workArea.settings.shared.loginMethodSelect}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                    {user.role.default_migration_role && (
                                        <Box columnLayout>
                                            <Heading variant="h2" underline>
                                                {t("Settings.newsEmailAlertTitle")}
                                            </Heading>

                                            <Box columnLayout gap="Xs">
                                                <TextBlock disableBottomSpacing>
                                                    {t("Settings.settingsEmailAlertText")}
                                                </TextBlock>

                                                <Box verticalAlignment="Center" gap="Xs">
                                                    {t("Settings.newsEmailAlert")}

                                                    <Checkbox
                                                        name={"news_email_alert"}
                                                        checked={values.newsEmailAlerts}
                                                        onChange={(e) =>
                                                            setFieldValue("newsEmailAlerts", e.target.checked)
                                                        }
                                                        disabled={tenantDetails.contractType == "TRIAL" || isSubmitting}
                                                        testId={
                                                            testIds.workArea.settings.shared.newsEmailAlertsCheckbox
                                                        }
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}

                                    {tenantDetails.featureLicenses.includes("FEATURE_PUBLIC_APIS") && (
                                        <Box columnLayout>
                                            <Heading variant="h2" underline>
                                                {t("Settings.cidrConfiguration.title")}
                                            </Heading>

                                            <Box columnLayout gap="Xs">
                                                <TextBlock disableBottomSpacing>
                                                    {t("Settings.cidrConfiguration.note")}
                                                </TextBlock>

                                                <Button
                                                    variant={"Tertiary"}
                                                    type="button"
                                                    icon={<Add />}
                                                    onClick={() => setFieldValue("cidrs", [...values.cidrs, ""])}
                                                    disabled={
                                                        values.cidrs.length >= MAX_CIDR_BLOCK_FIELDS || isSubmitting
                                                    }
                                                    testId={testIds.workArea.settings.shared.cidrs.addNewButton}
                                                >
                                                    {t("Settings.cidrConfiguration.addCidrBlock")}
                                                </Button>

                                                <Box columnLayout>
                                                    {values.cidrs.map((cidr, index) => (
                                                        <Box key={index} padding="Xxs" gap="Xs" verticalAlignment="Top">
                                                            <TextInput
                                                                value={cidr}
                                                                name={"cidr"}
                                                                onChange={(e) =>
                                                                    setFieldValue(`cidrs[${index}]`, e.target.value)
                                                                }
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                error={
                                                                    submitCount > 0
                                                                        ? getIn(errors, `cidrs[${index}]`)
                                                                        : undefined
                                                                }
                                                                width={320}
                                                                disabled={isSubmitting}
                                                                testId={`${testIds.workArea.settings.shared.cidrs.inputField}-${index}`}
                                                            />

                                                            <IconButton
                                                                deleteButton
                                                                onClick={() =>
                                                                    setFieldValue(
                                                                        "cidrs",
                                                                        values.cidrs.filter((_, i) => i !== index)
                                                                    )
                                                                }
                                                                disabled={isSubmitting}
                                                                testId={`${testIds.workArea.settings.shared.cidrs.deleteButton}-${index}`}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                {values.cidrs.length >= MAX_CIDR_BLOCK_FIELDS && (
                                                    <TextBlock disableBottomSpacing className={style.errorText}>
                                                        {t("Settings.cidrConfiguration.maxLimitMessage", {
                                                            cidrCount: MAX_CIDR_BLOCK_FIELDS,
                                                        })}
                                                    </TextBlock>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Form>
                        </SubPageLayout>
                    );
                }}
            </Formik>

            <Modal
                isOpen={isCancelModalVisible}
                hideModal={() => setIsCancelModalVisible(false)}
                title={t("Common.unsavedChanges.title")}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setIsCancelModalVisible(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.unsavedChanges.keepEditing")}
                        </Button>

                        <Button
                            variant="Secondary"
                            onClick={() => {
                                formikRef.current?.resetForm();
                                setIsCancelModalVisible(false);
                            }}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("Common.unsavedChanges.dontSave")}
                        </Button>
                    </>
                }
            >
                {t("Common.unsavedChanges.warning")}
            </Modal>

            <AlertModal
                title={t("Settings.errorTitle")}
                variant={"Error"}
                isOpen={isAlertModalVisible}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setAlertModalVisible(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
                hideModal={() => setAlertModalVisible(false)}
            >
                {alertModalMessage}
            </AlertModal>
        </>
    );
};
